// src/pages/KuliahSinglePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    query,
    Timestamp,
    where,
    orderBy,
} from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import { toYMD, expandRecurringRules } from "../utils/recurringUtils";
import { getHariLong, getTarikh, capitalizeWords } from "../utils/formatHelpers";

const normalizeKuliah = (raw = {}, fallbackId = "") => {
    const start =
        raw.start?.toDate
            ? raw.start.toDate()
            : raw.start?.seconds
                ? new Date(raw.start.seconds * 1000)
                : raw.start instanceof Date
                    ? raw.start
                    : raw.start
                        ? new Date(raw.start)
                        : null;

    const date =
        raw.date instanceof Date
            ? raw.date
            : start instanceof Date && !isNaN(start.getTime())
                ? start
                : null;

    return {
        ...raw,
        eventId: raw.eventId || fallbackId || raw.id || "",
        start,
        date,
        programText: raw.programText || raw.program || raw.title || "Kuliah",
        ustazText: raw.ustazText || raw.speaker || raw.nama_ustaz || "",
        subjectText: raw.subjectText || raw.subjek || "Umum",
        titleText: raw.titleText || raw.tajuk || raw.title || "",
    };
};

export default function KuliahSinglePage() {
    const { slug, k } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const s = useMemo(() => String(slug || "").trim().toLowerCase(), [slug]);
    const key = useMemo(() => String(k || "").trim(), [k]);

    // ===== kalau datang dari MasjidPublicHome (in-app click)
    const preMasjid = location.state?.masjid || null;
    const preKuliahRaw = location.state?.kuliah || null;
    const preKey = location.state?.kKey || null;

    const [masjid, setMasjid] = useState(null);
    const [kuliah, setKuliah] = useState(null);
    const [loadingMasjid, setLoadingMasjid] = useState(true);
    const [loadingKuliah, setLoadingKuliah] = useState(true);

    // ===== 1) Resolve MASJID (state-first, fallback fetch)
    useEffect(() => {
        let alive = true;

        const preSlug =
            String(preMasjid?.profail?.slug || preMasjid?.slug || "")
                .trim()
                .toLowerCase() || "";

        if (preMasjid && preSlug && preSlug === s) {
            setMasjid(preMasjid);
            setLoadingMasjid(false);
            return () => { alive = false; };
        }

        (async () => {
            if (!s) {
                setMasjid(null);
                setLoadingMasjid(false);
                return;
            }

            setLoadingMasjid(true);
            setMasjid(null);

            try {
                const qRef = query(
                    collection(dbAdmin, "masajid"),
                    where("profail.slug", "==", s),
                    limit(1)
                );
                const snap = await getDocs(qRef);
                if (!alive) return;

                if (snap.empty) {
                    setMasjid(null);
                    setLoadingMasjid(false);
                    return;
                }

                const d = snap.docs[0];
                setMasjid({ id: d.id, ...(d.data() || {}) });
                setLoadingMasjid(false);
            } catch (e) {
                console.error("❌ load masjid error:", e);
                setMasjid(null);
                setLoadingMasjid(false);
            }
        })();

        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [s]);

    const masjidId = masjid?.id || "";

    // ===== 2) Resolve KULIAH (state-first, fallback fetch)
    useEffect(() => {
        let alive = true;

        // kalau state match → terus guna (no fetch)
        if (preKuliahRaw && preKey && String(preKey) === String(key)) {
            setKuliah(normalizeKuliah(preKuliahRaw, String(preKey)));
            setLoadingKuliah(false);
            return () => { alive = false; };
        }

        if (!masjidId || !key) {
            setKuliah(null);
            setLoadingKuliah(false);
            return () => { alive = false; };
        }

        (async () => {
            setLoadingKuliah(true);
            setKuliah(null);

            try {
                // A) key = t__ms (fallback legacy)
                if (key.startsWith("t__")) {
                    const ms = Number(key.slice(3));
                    if (Number.isFinite(ms) && ms > 0) {
                        const a = Timestamp.fromDate(new Date(ms - 60 * 1000));
                        const b = Timestamp.fromDate(new Date(ms + 60 * 1000));

                        const q2 = query(
                            collection(dbAdmin, "masajid", masjidId, "kuliah"),
                            where("start", ">=", a),
                            where("start", "<=", b),
                            orderBy("start", "asc"),
                            limit(8)
                        );
                        const snap2 = await getDocs(q2);
                        if (!alive) return;

                        if (!snap2.empty) {
                            let best = null;
                            let bestDiff = Infinity;

                            snap2.docs.forEach((d) => {
                                const obj = normalizeKuliah(d.data(), d.id);
                                const t = obj?.date?.getTime?.() || 0;
                                const diff = Math.abs(t - ms);
                                if (diff < bestDiff) {
                                    bestDiff = diff;
                                    best = obj;
                                }
                            });

                            setKuliah(best);
                            setLoadingKuliah(false);
                            return;
                        }
                    }
                }

                // B) recurring key: seriesId__YYYY-MM-DD
                const idx = key.indexOf("__");
                const maybeYmd = idx > -1 ? key.slice(idx + 2) : "";
                const isRecurringKey = idx > -1 && /^\d{4}-\d{2}-\d{2}$/.test(maybeYmd);

                if (isRecurringKey) {
                    const seriesId = key.slice(0, idx);
                    const ymd = maybeYmd;

                    // try getDoc by docId
                    let rule = null;
                    const ruleDoc = await getDoc(
                        doc(dbAdmin, "masajid", masjidId, "kuliahRecurring", seriesId)
                    );
                    if (ruleDoc.exists()) {
                        rule = { ...ruleDoc.data(), seriesId: ruleDoc.data()?.seriesId || ruleDoc.id };
                    }

                    // fallback query by field seriesId
                    if (!rule) {
                        const qy = query(
                            collection(dbAdmin, "masajid", masjidId, "kuliahRecurring"),
                            where("seriesId", "==", seriesId),
                            limit(1)
                        );
                        const snap = await getDocs(qy);
                        if (!snap.empty) {
                            const d = snap.docs[0];
                            rule = { ...d.data(), seriesId: d.data()?.seriesId || d.id };
                        }
                    }

                    if (!alive) return;

                    if (!rule) {
                        setKuliah(null);
                        setLoadingKuliah(false);
                        return;
                    }

                    const targetDate = new Date(`${ymd}T00:00:00`);
                    const targetMs = targetDate.getTime();

                    // expand sekitar tarikh yang dicari, bukan sekitar "now"
                    const expanded = expandRecurringRules([rule], masjidId, targetMs - 24 * 60 * 60 * 1000, 3);
                    const hit = expanded.find((ev) => toYMD(ev.start) === ymd) || null;

                    setKuliah(hit ? normalizeKuliah(hit, key) : null);
                    setLoadingKuliah(false);
                    return;
                }

                // C) single: cuba getDoc (docId = key)
                const singleDoc = await getDoc(doc(dbAdmin, "masajid", masjidId, "kuliah", key));
                if (!alive) return;

                if (singleDoc.exists()) {
                    setKuliah(normalizeKuliah(singleDoc.data(), singleDoc.id));
                    setLoadingKuliah(false);
                    return;
                }

                // D) single: query by field eventId == key
                const qy = query(
                    collection(dbAdmin, "masajid", masjidId, "kuliah"),
                    where("eventId", "==", key),
                    limit(1)
                );
                const snap = await getDocs(qy);
                if (!alive) return;

                if (!snap.empty) {
                    const d = snap.docs[0];
                    setKuliah(normalizeKuliah(d.data(), d.id));
                    setLoadingKuliah(false);
                    return;
                }

                setKuliah(null);
                setLoadingKuliah(false);
            } catch (e) {
                console.error("❌ load kuliah error:", e);
                setKuliah(null);
                setLoadingKuliah(false);
            }
        })();

        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [masjidId, key]);

    const loading = loadingMasjid || loadingKuliah;

    if (loading) return <div style={{ padding: 18 }}>Loading...</div>;
    if (!masjid) return <div style={{ padding: 18 }}>Masjid tidak dijumpai.</div>;

    const p = masjid?.profail || {};
    const masjidName = capitalizeWords(p?.nama || masjid?.nama || "Masjid");
    const masjidLogo = p?.logo || p?.logoJa || p?.gambar || masjid?.logo || masjid?.gambar || "";
    const bannerImg = p?.gambar || masjid?.gambar || masjidLogo || "";
    const banner = (kuliah?.poster || bannerImg) || "";

    return (
        <div style={{ padding: 14, maxWidth: 520, margin: "0 auto" }}>
            {!kuliah ? (
                <div className="masjid-empty" style={{ padding: 12 }}>
                    <div style={{ width: "100%", borderRadius: 14, overflow: "hidden" }}>
                        <img
                            src={bannerImg || masjidLogo || "/images/150.png"}
                            alt={masjidName || "Masjid"}
                            style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
                            onError={(e) => {
                                e.currentTarget.src = "/images/150.png";
                            }}
                        />
                    </div>
                </div>

            ) : (
                <div
                    className="card"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        borderRadius: 18,
                    }}
                >
                    {/* ✅ Banner lebih besar */}
                    <div
                        className="card-image"
                        style={{
                            height: 190,
                            borderTopLeftRadius: 18,
                            borderTopRightRadius: 18,
                            overflow: "hidden",
                        }}
                    >
                        {banner ? (
                            <img
                                src={banner}
                                alt="Kuliah"
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                        ) : (
                            <div className="card-image-fallback" style={{ height: "100%" }} />
                        )}
                    </div>

                    <div
                        className="card-content"
                        style={{
                            flex: "1 1 auto",
                            display: "flex",
                            flexDirection: "column",
                            padding: "14px 16px 16px",
                            gap: 10,
                            alignItems: "center",
                            textAlign: "center",
                        }}
                    >
                        <div className="mosque-row" style={{ marginBottom: 2, marginTop: 50, justifyContent: "center" }}>
                            {masjidLogo && (
                                <img
                                    className="mosque-logo"
                                    src={masjidLogo}
                                    alt="Logo masjid"
                                    style={{ width: 38, height: 38, borderRadius: 8, objectFit: "cover" }}
                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                />
                            )}
                            <p className="mosque-name-body" style={{ fontSize: 15, margin: 0, opacity: 0.75 }}>
                                {masjidName}
                            </p>
                        </div>

                        {/* ✅ Tajuk & tarikh lebih besar */}
                        <div className="card-header" style={{ padding: 0 }}>
                            <h3 style={{ margin: 0, fontSize: 20, lineHeight: 1.2, fontWeight: 900 }}>
                                {kuliah.programText}
                            </h3>

                            <p className="date" style={{ margin: "8px 0 0", fontSize: 14, fontWeight: 700 }}>
                                {kuliah.date ? `${getHariLong(kuliah.date)}, ${getTarikh(kuliah.date)}` : ""}
                            </p>

                            <p className="ustaz-name" style={{ margin: "6px 0 0", fontSize: 24, fontWeight: 700, opacity: 0.9 }}>
                                {kuliah.ustazText}
                            </p>
                        </div>

                        {/* ✅ Kitab & tajuk lebih “penuh” */}
                        <div
                            className={`card-footer ${kuliah.kitabCoverImage ? "has-kitab" : ""}`}
                            style={{
                                minHeight: 88,
                                alignItems: "center",
                                paddingTop: 10,
                                gap: 12,
                                flexDirection: "column"
                            }}
                        >


                            <div className="footer-right" style={{ paddingTop: 2, textAlign: "center" }}>
                                <div className="subject" style={{ marginBottom: 8 }}>
                                    <span
                                        className="subject-label"
                                        style={{
                                            fontSize: 13,
                                            padding: "6px 12px",
                                            borderRadius: 999,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {kuliah.subjectText}
                                    </span>
                                </div>

                                <p className="title" style={{ margin: 0, fontSize: 16, lineHeight: 1.25, fontWeight: 800 }}>
                                    {kuliah.titleText}
                                </p>
                            </div>
                            {kuliah.kitabCoverImage && (
                                <img
                                    className="kitab-cover"
                                    src={kuliah.kitabCoverImage}
                                    alt={kuliah.kitabTitle || "Kitab"}
                                    style={{
                                        width: 128,
                                        height: 178,
                                        borderRadius: 10,
                                        objectFit: "cover",
                                        flex: "0 0 auto",
                                    }}
                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                />
                            )}
                        </div>
                    </div>

                    {/* Avatar kekal, tapi bagi nampak kemas */}
                    <div
                        className="avatar-wrapper"
                        style={{
                            position: "absolute",
                            top: 100,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 150,
                            height: 150,
                        }}
                    >
                        <div className="avatar" style={{ width: 140, height: 140, borderWidth: 4 }}>
                            <img
                                src={kuliah.speakerImage || kuliah.gambar_ustaz || ""}
                                alt="Ustaz"
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                        </div>
                    </div>
                </div>

            )}

            {/* ✅ Ke utama bawah card, center */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
                <button className="kuliah-share-btn" onClick={() => navigate(`/l/${encodeURIComponent(s)}`, { replace: true })}>
                    Ke utama
                </button>
            </div>
        </div>
    );
}
